import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Rocket } from 'lucide-react';
import '../styles/organizer-cards.css'; 
import imgFR from '../assets/Images/Organizatori/FR.webp';
import imgIT from '../assets/Images/Organizatori/IT.webp';
import imgMO from '../assets/Images/Organizatori/MO.webp';
import imgPAX from '../assets/Images/Organizatori/PAX.webp';
import imgPR from '../assets/Images/Organizatori/PR.webp';
import imgDR from '../assets/Images/Organizatori/DR.webp';
import imgTre from '../assets/Images/Organizatori/rade.webp';
import imgSek from '../assets/Images/Organizatori/kami.webp';
import imgPre from '../assets/Images/Organizatori/evg.webp';
import imgFRc from '../assets/Images/Organizatori/angbt.webp';
import imgITc from '../assets/Images/Organizatori/iljo.webp';
import imgHRc from '../assets/Images/Organizatori/ciljana.webp';

const organizers = [
  {
    id: 1,
    name: 'Анастасија Мицова',
    role: 'Главен Организатор',
    image: imgMO
  },
  {
    id: 2,
    name: 'Марко Панев',
    role: 'Одговорен За Учесници',
    image: imgPAX
  },
  {
    id: 3,
    name: 'Ева Грозданоска',
    role: 'Одговорна За Односи Со Компании',
    image: imgFR
  },
  {
    id: 4,
    name: 'Сара Стојановска',
    role: 'Одговорна За Односи Со Јавност',
    image: imgPR
  },
  {
    id: 5,
    name: 'Александар Грозданоски',
    role: 'Одговорен За Интернет Технологии',
    image: imgIT
  },
  {
    id: 6,
    name: 'Маријана Ѓорѓиевска',
    role: 'Одговорна За Дизајн',
    image: imgDR
  },
  {
    id: 7,
    name: 'Евгенија Попчановска',
    role: 'Претседател',
    image: imgPre
  },
  {
    id: 8,
    name: 'Раде Мицов',
    role: 'Потпретседател',
    image: imgTre
  },
  {
    id: 9,
    name: 'Камелија Матеска',
    role: 'Секретар',
    image: imgSek
  },
  {
    id: 10,
    name: 'Бојана Цилевска',
    role: 'Координатор за човечки ресурси',
    image: imgHRc
  },
  {
    id: 11,
    name: 'Ангела Крстановска',
    role: 'Координатор за односи со компании',
    image: imgFRc
  },
  {
    id: 12,
    name: 'Илија Крстески',
    role: 'Координатор за информациски технологии',
    image: imgITc
  }
];

export default function OrganizerCards({ customOrganizers, headerTitle = "ORGANIZERS", headerSubtitle = "Space Mission Through Our Team", completionMessage = "✨ Патувањето заврши! ✨" }) {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(false);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const touchStartX = useRef(0);

  // Use custom organizers if provided, otherwise use default
  const displayOrganizers = customOrganizers || organizers;

  // Lock body scroll when tour is active, unlock when completed
  useEffect(() => {
    if (!tourCompleted) {
      // Disable body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      // Re-enable body scroll
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [tourCompleted]);

  // Handle scroll wheel to advance to next organizer
  const handleWheel = (e) => {
    if (isAnimating) {
      e.preventDefault();
      return;
    }
    
    // If tour is completed and user scrolls up, re-engage the tour
    if (tourCompleted && e.deltaY < 0) {
      e.preventDefault();
      setTourCompleted(false);
      setActiveIndex(displayOrganizers.length - 1); // Start from last organizer
      return;
    }

    // If tour is completed and scrolling down, allow natural scroll
    if (tourCompleted) {
      return; // Don't prevent default, allow page to scroll
    }

    // Allow scrolling up when at first organizer
    if (e.deltaY < 0 && activeIndex === 0) {
      return; // Allow natural upward scroll
    }

    // Block all other scrolling until tour is complete
    e.preventDefault();

    // If at last organizer and scrolling down, complete the tour
    if (e.deltaY > 0 && activeIndex === displayOrganizers.length - 1) {
      setTourCompleted(true);
      return;
    }
    
    if (e.deltaY > 0 && activeIndex < displayOrganizers.length - 1) {
      // Scrolling down - go to next organizer
      setIsAnimating(true);
      setActiveIndex(prev => prev + 1);
      setTimeout(() => setIsAnimating(false), 600);
    } else if (e.deltaY < 0 && activeIndex > 0) {
      // Scrolling up - go to previous organizer
      setIsAnimating(true);
      const newIndex = Math.max(0, activeIndex - 1);
      setActiveIndex(newIndex);
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  // Native touch handlers for mobile - much smoother than framer-motion drag
  const handleTouchStart = (e) => {
    if (tourCompleted) return;
    touchStartY.current = e.touches[0].clientY;
    touchStartX.current = e.touches[0].clientX;
    touchStartTime.current = Date.now();
  };

  const handleTouchEnd = (e) => {
    if (tourCompleted || isAnimating) {
      return;
    }
    
    const touchEndY = e.changedTouches[0].clientY;
    const touchEndX = e.changedTouches[0].clientX;
    const deltaY = touchStartY.current - touchEndY;
    const deltaX = touchStartX.current - touchEndX;
    const duration = Date.now() - touchStartTime.current;
    
    // Check if it's a vertical swipe (not horizontal)
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      return; // Ignore horizontal swipes
    }
    
    const swipeThreshold = 50;
    const quickSwipeTime = 300;
    
    // Quick swipe or significant distance
    const isQuickSwipe = duration < quickSwipeTime && Math.abs(deltaY) > 30;
    const isLongSwipe = Math.abs(deltaY) > swipeThreshold;
    
    if (!isQuickSwipe && !isLongSwipe) {
      return;
    }
    
    if (deltaY > 0) {
      // Swiped up - next organizer
      if (activeIndex < displayOrganizers.length - 1) {
        setIsAnimating(true);
        setActiveIndex(prev => prev + 1);
        setTimeout(() => setIsAnimating(false), 400);
      } else {
        // Last organizer - complete tour
        setTourCompleted(true);
      }
    } else {
      // Swiped down - previous organizer
      if (activeIndex > 0) {
        setIsAnimating(true);
        setActiveIndex(prev => prev - 1);
        setTimeout(() => setIsAnimating(false), 400);
      }
    }
  };

  // Calculate zig-zag positions for the path
  const pathNodes = displayOrganizers.map((_, index) => {
    const yPos = (index / (displayOrganizers.length - 1)) * 100;
    const xOffset = index % 2 === 0 ? -30 : 30; // Zig-zag left and right
    return { x: xOffset, y: yPos };
  });

  // Get current node position safely
  const currentNode = pathNodes[activeIndex] || pathNodes[0];

  return (
    <div 
      ref={containerRef} 
      className={`relative ${tourCompleted ? 'min-h-screen' : 'h-screen overflow-hidden'} bg-[#E4570B] retro-grid scanlines`}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{ 
        touchAction: tourCompleted ? 'auto' : 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent'
      }}
    >
      {/* Header - visible on mobile, hidden on desktop */}
      <div className="absolute top-0 left-0 right-0 z-50 py-6 px-6 text-center backdrop-blur-sm bg-[#E4570B]/80 border-b-2 border-[#EFA74F]/30 lg:hidden">
        <h1 className="text-[#DFC293] tracking-[0.3em] neon-text uppercase">{headerTitle}</h1>
        <div className="flex items-center justify-center gap-3 mt-3">
          <div className="h-[2px] w-12 bg-gradient-to-r from-transparent via-[#EFA74F] to-transparent"></div>
          <p className="text-[#EFA74F] tracking-widest text-sm uppercase">{headerSubtitle}</p>
          <div className="h-[2px] w-12 bg-gradient-to-r from-transparent via-[#EFA74F] to-transparent"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`${tourCompleted ? 'relative py-24' : 'absolute top-4 lg:top-0 bottom-0'} left-0 right-0 flex items-start justify-center px-4 lg:px-12 pt-24 lg:pt-16`}>
        <div className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-16 relative">
          
          {/* Organizer Card - Fixed position on left, just fades in/out */}
          <div className="relative w-full max-w-sm lg:max-w-lg z-10 order-1" style={{ height: '420px', position: 'relative' }}>
            {displayOrganizers.map((organizer, index) => (
              <motion.div
                key={organizer.id}
                animate={{
                  opacity: activeIndex === index ? 1 : 0
                }}
                initial={false}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="glass-panel glow-golden rounded-xl p-6 lg:p-8 text-center flex flex-col items-center justify-center overflow-hidden"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: activeIndex === index ? 'auto' : 'none',
                  clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 20px), calc(100% - 20px) 100%, 0 100%)',
                  willChange: 'opacity'
                }}
              >
                {/* Geometric corner decorations */}
                <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-[#EFA74F] opacity-60"></div>
                <div className="absolute top-0 right-0 w-16 h-16 border-t-4 border-r-4 border-[#749269] opacity-60"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 border-b-4 border-l-4 border-[#749269] opacity-60"></div>
                <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-[#EFA74F] opacity-60"></div>

                {/* Hexagon frame for photo */}
                <div className="relative w-48 h-48 lg:w-52 lg:h-52 mx-auto mb-4 lg:mb-6">
                  {/* Hexagonal container */}
                  <div 
                    className="absolute inset-0"
                    style={{
                      clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)'
                    }}
                  >
                    {/* Image - load current +/- 1 eagerly for smooth mobile */}
                    <img
                      src={organizer.image}
                      alt={organizer.name}
                      className="w-full h-full object-cover"
                      loading={
                        Math.abs(index - activeIndex) <= 1 ? 'eager' : 'lazy'
                      }
                      decoding="async"
                      style={{
                        transform: `scale(${
                          index === 0 ? 1.65 : 
                          index === 6 ? 1 :
                          index === 7 ? 1.5 :
                          index === 8 ? 1.3 :
                          index === 9 ? 1.3 :
                          index === 10 ? 1.4 :
                          index === 11 ? 1.6 :
                          1.45
                        })`, 
                        transformOrigin: 'center center', 
                        transition: 'transform 0.6s ease',
                        objectPosition: index === 6 ? 'center 0%' : 
                                       index === 7 ? 'center -40%' : 
                                       index === 8 ? 'center -20%' :
                                       index === 9 ? 'center 35%' :
                                       index === 10 ? 'center 20%' :
                                       index === 11 ? 'center -30%' :
                                       'center center'
                      }}
                    />
                  </div>
                  
                  {/* Hexagonal border */}
                  <div 
                    className="absolute inset-0 bg-gradient-to-br from-[#EFA74F] via-[#749269] to-[#EFA74F]"
                    style={{
                      clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
                      padding: '3px',
                      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      WebkitMaskComposite: 'xor',
                      maskComposite: 'exclude'
                    }}
                  >
                  </div>
                  
                  {/* Animated scan line over photo - only animate when active */}
                  {activeIndex === index && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-b from-transparent via-[#EFA74F] to-transparent pointer-events-none"
                      style={{
                        clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
                        height: '4px',
                        opacity: 0.4,
                        willChange: 'transform'
                      }}
                      animate={{
                        y: [0, 192, 0]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    />
                  )}

                  {/* Corner tech elements - only animate when active */}
                  {activeIndex === index && (
                    <>
                      <motion.div
                        className="absolute -top-3 -left-3 w-8 h-8"
                        animate={{
                          opacity: [0.4, 1, 0.4]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      >
                        <div className="w-full h-1 bg-[#EFA74F]"></div>
                        <div className="w-1 h-full bg-[#EFA74F] absolute top-0 left-0"></div>
                      </motion.div>
                      
                      <motion.div
                        className="absolute -bottom-3 -right-3 w-8 h-8"
                        animate={{
                          opacity: [0.4, 1, 0.4]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: 1
                        }}
                      >
                        <div className="w-full h-1 bg-[#EFA74F] absolute bottom-0"></div>
                        <div className="w-1 h-full bg-[#EFA74F] absolute bottom-0 right-0"></div>
                      </motion.div>
                    </>
                  )}
                </div>

                {/* Name with neon effect */}
                <h2 className="text-[#DFC293] mb-2 neon-text tracking-wide uppercase">{organizer.name}</h2>

                {/* Role */}
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-[#EFA74F] blur-sm opacity-50"></div>
                  <p className="text-[#EFA74F] relative tracking-widest uppercase text-sm">{organizer.role}</p>
                </div>

                {/* Tech-style progress bars */}
                <div className="flex justify-center gap-3 mt-6 w-full max-w-[200px]">
                  {[...Array(5)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="flex-1 h-1 bg-[#6A310A] relative overflow-hidden"
                      style={{
                        boxShadow: 'inset 0 0 4px rgba(0,0,0,0.5)'
                      }}
                    >
                      <motion.div
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#749269] to-[#EFA74F]"
                        initial={{ width: '0%' }}
                        animate={{
                          width: activeIndex === index ? '100%' : '0%'
                        }}
                        transition={{
                          duration: 0.8,
                          delay: i * 0.1
                        }}
                      />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Rocket Path - Right side on desktop, background on mobile */}
          <div className="absolute lg:relative order-2 inset-0 lg:inset-auto w-full lg:max-w-md h-full lg:h-[70vh] opacity-30 lg:opacity-100 pointer-events-none">
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 200 400"
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Path line with glow */}
              <defs>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
                <linearGradient id="pathGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#EFA74F" stopOpacity="0.8"/>
                  <stop offset="50%" stopColor="#DFC293" stopOpacity="0.6"/>
                  <stop offset="100%" stopColor="#749269" stopOpacity="0.8"/>
                </linearGradient>
              </defs>

              {/* Zig-zag path */}
              <path
                d={`M 100 20 ${pathNodes.map((node, i) => 
                  `L ${100 + node.x} ${20 + (node.y * 3.6)}`
                ).join(' ')}`}
                fill="none"
                stroke="url(#pathGradient)"
                strokeWidth="2"
                filter="url(#glow)"
                opacity="0.8"
              />

              {/* Nodes - Each represents an organizer */}
              {pathNodes.map((node, index) => (
                <g key={index}>
                  {/* Outer glow circle */}
                  <circle
                    cx={100 + node.x}
                    cy={20 + (node.y * 3.6)}
                    r="12"
                    fill={activeIndex >= index ? '#EFA74F' : '#6A310A'}
                    opacity="0.3"
                    filter="url(#glow)"
                  />
                  {/* Main node */}
                  <circle
                    cx={100 + node.x}
                    cy={20 + (node.y * 3.6)}
                    r="6"
                    fill={activeIndex >= index ? '#EFA74F' : '#6A310A'}
                    stroke="#DFC293"
                    strokeWidth="2"
                    opacity={activeIndex >= index ? 1 : 0.5}
                  />
                  {/* Pulse effect for active node */}
                  {activeIndex === index && (
                    <motion.circle
                      cx={100 + node.x}
                      cy={20 + (node.y * 3.6)}
                      r="10"
                      fill="none"
                      stroke="#EFA74F"
                      strokeWidth="2"
                      initial={{ scale: 0.8, opacity: 0.8 }}
                      animate={{ scale: 1.5, opacity: 0 }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                  )}
                </g>
              ))}
            </svg>

            {/* Animated Rocket - travels to each node */}
            <motion.div
              className="absolute"
              animate={{
                left: '50%',
                top: '5%',
                x: `${currentNode.x}px`,
                y: `${currentNode.y * 3.6}px`,
                rotate: activeIndex % 2 === 0 ? -15 : 15
              }}
              transition={{
                duration: 0.8,
                ease: "easeOut"
              }}
            >
              <div className="relative -translate-x-1/2 -translate-y-1/2">
                {/* Rocket glow */}
                <div className="absolute inset-0 blur-lg">
                  <Rocket className="w-12 h-12 text-[#EFA74F]" fill="#EFA74F" />
                </div>
                {/* Rocket */}
                <Rocket className="w-12 h-12 text-[#DFC293] relative z-10" fill="#DFC293" />
                
                {/* Flame trail */}
                <motion.div
                  className="absolute top-full left-1/2 -translate-x-1/2 w-1 bg-gradient-to-b from-[#EFA74F] via-[#E4570B] to-transparent"
                  style={{ height: '40px' }}
                  animate={{
                    opacity: [0.6, 1, 0.6],
                    scaleY: [1, 1.2, 1]
                  }}
                  transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </div>
            </motion.div>
          </div>

        </div>

      </div>

      {/* Scroll indicator */}
      {activeIndex < displayOrganizers.length - 1 && (
        <motion.div
          className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60]"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{ pointerEvents: 'none' }}
        >
          <div className="flex flex-col items-center gap-2">
            <p className="text-[#DFC293] text-sm">Scroll to explore</p>
            <motion.div
              className="w-6 h-10 border-2 border-[#EFA74F] rounded-full p-1"
              animate={{ borderColor: ['#EFA74F', '#DFC293', '#EFA74F'] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <motion.div
                className="w-1.5 h-1.5 bg-[#EFA74F] rounded-full mx-auto"
                animate={{ y: [0, 20, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            </motion.div>
          </div>
        </motion.div>
      )}

      {/* Progress dots indicator */}
      {!tourCompleted && (
        <div className="fixed right-8 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-4" style={{ pointerEvents: 'auto' }}>
          {displayOrganizers.map((_, index) => (
            <motion.div
              key={index}
              className="w-3 h-3 rounded-full border-2 border-[#DFC293] cursor-pointer"
              style={{
                backgroundColor: activeIndex >= index ? '#EFA74F' : 'transparent',
                pointerEvents: 'auto'
              }}
              animate={{
                scale: activeIndex === index ? 1.3 : 1,
                opacity: activeIndex >= index ? 1 : 0.5
              }}
              onClick={() => {
                if (!isAnimating) {
                  setIsAnimating(true);
                  setActiveIndex(index);
                  setTimeout(() => setIsAnimating(false), 800);
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
